/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Icon, IconButton } from '@chakra-ui/react';
import { TbMaximize } from 'react-icons/tb';

import Dropdown from '../uiold/Dropdown';
import MapView, { Marker } from '../uiold/MapView';
import connectData, { ConnectedProps } from '../../support/connectData';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { Text, TextInputSearch } from '../uiold';
import { themeColors } from '../uiold/Constants';

import getImageSize from '../../support/getImageSize';
import getDefaultSelectedDate from '../../support/getDefaultSelectedDate';
import getVendorPlacement from '../../support/helpers/getVendorPlacement';
import vendorSearchMatch from '../../support/helpers/vendorSearchMatch';
import hasProductsField from '../../support/helpers/hasProductsField';
import getColorString from '../../support/helpers/getColorString';

import { Market, Position } from '../../types/shared/Market';
import { MapSettings } from '../../types/shared/Account';
import {
  VendorList,
  VendorSummaryWithMarkets as Vendor,
} from '../../types/shared/Vendor';
import { PlacementByVendor } from '../../types/shared/Placement';
import { PositionMapByMarket } from '../../types/shared/PositionMap';
import SignupFormModel from '../FormBuilder/SignupFormModel';
import formatDate from '../../core/support/formatDate';

const MIN_LIST_WIDTH = 200;
const MIN_MAP_WIDTH = 500;
const ERROR_LOADING_MAP = 'Error: unable to load map';
const ERROR_NO_MAP = 'Error: no map for this market';

type ContainerProps = {
  accountID: string;
  marketID: string;
  isFullScreen: boolean;
  onFullScreenToggle: () => void;
  onProfileSelect: (vendorID: string) => void;
};

type Props = {
  accountID: string;
  marketID: string;
  market: Market;
  vendors: VendorList;
  position: PositionMapByMarket;
  placement: PlacementByVendor;
  formDef: SignupFormModel;
  isFullScreen: boolean;
  mapSettings?: MapSettings;
  onFullScreenToggle: () => void;
  onProfileSelect: (vendorID: string) => void;
};

type Dimensions = { width: number; height: number };

// This just displays a loading indicator while waiting for the data to load,
// then renders the MapEmbed.
function MapEmbedContainer(props: ContainerFullProps) {
  let {
    market,
    vendors,
    position,
    placement,
    formDef,
    mapSettings,
    ...otherProps
  } = props;
  if (
    !market ||
    !vendors ||
    !position ||
    !placement ||
    !formDef ||
    !mapSettings
  ) {
    return <LoadingIndicator />;
  } else {
    return (
      <MapEmbed
        market={market}
        vendors={vendors}
        position={position}
        placement={placement}
        mapSettings={mapSettings}
        formDef={
          // TODO: TypeScript thinks this is of type FormModel | SignupFormModel
          formDef as SignupFormModel
        }
        {...otherProps}
      />
    );
  }
}

function MapEmbed(props: Props) {
  let {
    market,
    vendors,
    position,
    placement,
    formDef,
    mapSettings,
    onProfileSelect,
    onFullScreenToggle,
  } = props;
  let [selectedDate, setSelectedDate] = useState(() =>
    getDefaultSelectedDate(market.dates || []),
  );

  let [searchText, setSearchText] = useState('');
  let { width, height } = useWindowDimensions();
  let placementByIndex = getVendorPlacement(market, selectedDate, {
    vendors,
    placement,
  });
  let markers = market.map?.markers;
  let filteredMarkers = useMemo(() => {
    let results: Array<[number, Position, Vendor]> = [];
    (markers || []).forEach((marker, index) => {
      if (marker != null) {
        let vendor = placementByIndex[String(index)];
        if (vendor != null && vendorSearchMatch(vendor, searchText)) {
          results.push([index, marker, vendor]);
        }
      }
    });
    return results;
  }, [markers, searchText, placementByIndex]);
  let [showError, imageDimensions] = useImageDimensions(market);
  if (showError) {
    return (
      <Text type="label" style={styles.errorText}>
        {showError}
      </Text>
    );
  }
  if (!imageDimensions) {
    return <LoadingIndicator />;
  }
  let useMobileLayout = width < height || width < MIN_MAP_WIDTH;
  let { listStyle, mapStyle } = getStyles(
    useMobileLayout,
    width,
    imageDimensions,
  );
  let imageWidth = imageDimensions.width || 0;
  let imageHeight = imageDimensions.height || 0;
  let listView = filteredMarkers.map(([index, _marker, vendor]) => {
    let id = market.id;
    let mark = position[id];
    let key = mark ? mark[String(index)] : String(index + 1);
    let onPress = () => {
      vendor && onProfileSelect(vendor.id);
    };
    return (
      <View key={index} style={styles.vendorItem}>
        <View style={styles.vendorKey}>
          <Text style={styles.vendorKeyText}>{key}</Text>
        </View>
        <View style={styles.vendorNameWrapper}>
          <Text type="label" numberOfLines={1} onPress={onPress}>
            {vendor.name}
          </Text>
        </View>
      </View>
    );
  });

  let bottomLegend = useMobileLayout ? (
    <div className="legend" style={listStyle}>
      {listView}
    </div>
  ) : null;

  let dateOptions = market.dates.map((date) => ({
    id: date,
    name: formatDate(date, { fromUTC: true, includeDayOfWeek: true }),
  }));
  let dateSelector = (
    <Dropdown
      block={true}
      options={dateOptions}
      value={selectedDate}
      onChange={({ id }: { id: string }) => setSelectedDate(id)}
    />
  );

  return (
    <div>
      <div
        className="map-container"
        style={{
          backgroundColor: mapSettings?.backgroundColor
            ? getColorString(mapSettings.backgroundColor)
            : '',
        }}
      >
        <div className="legend" style={useMobileLayout ? { width } : listStyle}>
          <div className="title">{market.name}</div>
          <div className="initialism">{market.description}</div>

          {dateSelector}
          {hasProductsField(formDef) ? (
            <Text style={{ fontSize: 12, lineHeight: 2 }}>
              Search by name or product:
            </Text>
          ) : null}
          <TextInputSearch
            containerStyle={styles.searchTextInput}
            value={searchText}
            onChangeText={setSearchText}
          />
          {useMobileLayout ? null : listView}
        </div>
        <div className="controls" style={useMobileLayout ? { top: 95 } : {}}>
          <IconButton
            aria-label="Full Screen"
            icon={
              <Icon
                as={TbMaximize}
                fontSize={20}
                onClick={onFullScreenToggle}
              />
            }
            size="sm"
          />
        </div>
        <MapView
          className="map-pane"
          width={mapStyle.width}
          height={mapStyle.height}
          imageUrl={getImageURL(market)}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
        >
          {filteredMarkers.map(([index, marker, vendor]) => {
            let id = market.id;
            let mark = position[id];
            let key = mark ? mark[String(index)] : String(index + 1);
            let onPress = () => {
              vendor && onProfileSelect(vendor.id);
            };
            return (
              <Marker
                key={index}
                itemKey={index.toString()}
                left={marker.left}
                top={marker.top}
                onClick={onPress}
              >
                <View style={styles.marker}>
                  <Text style={styles.markerText}>{key}</Text>
                </View>
                {/* This may not be the best way to invert tooltip style to bottom
                but we will be removing bootstrap anyways */}
                <View style={styles.tooltip}>
                  <div className="tooltip bottom">
                    <div className="tooltip-arrow" />
                    <div
                      className="tooltip-inner"
                      style={{
                        backdropFilter: 'blur(12.7px)',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: 5,
                        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                        padding: '5px 10px',
                      }}
                    >
                      {vendor.name}
                      {'\n'}
                      (click for details)
                    </div>
                  </div>
                </View>
              </Marker>
            );
          })}
        </MapView>
        {bottomLegend}
      </div>
    </div>
  );
}

function useWindowDimensions() {
  let [dimensions, setDimensions] = useState(() => getWindowSize());
  useEffect(() => {
    let onResize = () => {
      let { width, height } = getWindowSize();
      setDimensions({ width, height });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [setDimensions]);
  return dimensions;
}

function useImageDimensions(market: Market) {
  let [error, setError] = useState('');
  let [dimensions, setDimensions] = useState<Dimensions | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!market.map || !market.map.id) {
      setError(ERROR_NO_MAP);
      return;
    }
    let url = getImageURL(market);
    // Note: Should we use helpers/loadImage?
    getImageSize(url, (error, size) => {
      if (cancelled) {
        return;
      }
      if (error) {
        setError(ERROR_LOADING_MAP);
      } else if (size) {
        let { width, height } = size;
        setDimensions({ width, height });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [market]);
  return [error, dimensions] as const;
}

function getStyles(
  useMobileLayout: boolean,
  windowWidth: number,
  imageDimensions: Dimensions,
) {
  let listWidth;
  let mapWidth;
  let totalHeight = 0;
  if (useMobileLayout) {
    if (imageDimensions) {
      totalHeight = Math.round(
        (imageDimensions.height / imageDimensions.width) * windowWidth,
      );
    }
    return {
      listStyle: {
        width: windowWidth,
        height: totalHeight / 2,
      },
      mapStyle: {
        width: windowWidth,
        height: totalHeight / 2,
      },
    };
  }
  listWidth = Math.round(windowWidth * 0.2);
  listWidth = Math.max(listWidth, MIN_LIST_WIDTH);
  mapWidth = windowWidth - listWidth;
  if (imageDimensions) {
    totalHeight = Math.round(
      (imageDimensions.height / imageDimensions.width) * mapWidth,
    );
  }
  return {
    listStyle: {
      width: listWidth,
      height: totalHeight,
    },
    mapStyle: {
      width: mapWidth,
      height: totalHeight,
    },
  };
}

function getWindowSize(): Dimensions {
  const htmlElement = window.document.documentElement;
  let width = htmlElement.clientWidth;
  let height = window.innerHeight;
  return { width, height };
}

function getImageURL({ map }: Market) {
  return map ? `/api/images/${map.id}/image.${map.imageType}` : '';
}

let styles = StyleSheet.create({
  errorText: {
    color: themeColors.red,
  },
  marker: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: [{ translateX: '-50%' }, { translateY: '-50%' }] as any,
    height: 38,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  markerText: {
    color: themeColors.white,
    fontSize: 12,
  },
  tooltip: {
    position: 'absolute',
    left: 16,
    top: 90,
  },
  vendorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 1,
  },
  vendorKey: {
    width: 33,
    height: 33,
    borderRadius: '50%' as any,
    borderWidth: 1,
    borderColor: '#000',
  },
  vendorKeyText: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: [{ translateX: '-50%' }, { translateY: '-50%' }] as any,
    fontSize: 12,
  },
  vendorNameWrapper: {
    flex: 1,
    paddingLeft: 5,
  },
  searchTextInput: {
    marginBottom: 10,
  },
});

const connector = connectData<ContainerProps>()((props) => ({
  market: {
    dataStoreKey: 'markets/:id',
    params: [props.marketID],
  },
  vendors: { dataStoreKey: 'vendors/active' },
  placement: { dataStoreKey: 'placement' },
  position: { dataStoreKey: 'position' },
  formDef: {
    dataStoreKey: 'forms/:id',
    params: ['signup'],
  },
  mapSettings: {
    dataStoreKey: 'accounts/:id/publicMapSettings',
    params: [props.accountID],
  },
}));
type ContainerFullProps = ConnectedProps<typeof connector>;
export default connector(MapEmbedContainer);
